import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';

import { eligibleExperiencesQueryOptions } from '@/api/queries/guided-experiences';
import { useAuth } from '@/hooks/use-auth';
import { useAuthStatus } from '@/hooks/use-auth-status';

import { resolveGate, type GateOutcome } from './resolve-gate';

/**
 * The single owner of the app's gate state (ADR 0006).
 *
 * "What the user must finish before the app opens" used to be expressed as
 * overlay z-order and ad-hoc boolean conjunctions spread across several
 * components. Here it is one value, gathered once, so the root layout's
 * `Stack.Protected` tiers, `FeatureTourLayer`'s mount condition and location
 * acquisition all read the same answer and cannot disagree.
 *
 * This module deliberately owns no branching: `resolveGate` is the pure decision
 * function and is covered exhaustively on its own. Everything below is wiring --
 * turning auth state and one query into `GateInput`.
 */

/** The gateway guided experience whose eligibility means onboarding is unfinished. */
const ONBOARDING_EXPERIENCE_ID = 'onboarding';

export type AppGate = {
  /** Which of the four mutually exclusive tiers the root layout should mount. */
  gate: GateOutcome;
  /** Cold boot: show the splash, mount nothing. */
  isBooting: boolean;
  /** The `(setup)` group owns the screen. */
  needsSetup: boolean;
  /**
   * The onboarding entry's server `current_step`, or `null` when onboarding is
   * not eligible or has not started. Not part of the gate decision -- carried
   * for `(setup)`'s dispatcher, which feeds it to `resolveSetupRoute`.
   */
  currentStep: number | null;
};

const AppGateContext = createContext<AppGate | undefined>(undefined);

function useAppGateValue(): AppGate {
  const authStatus = useAuthStatus();
  const { user } = useAuth();

  const userId = user?.id ?? '';
  // `usernamePending` rides the auth session, so it is synchronous with auth and
  // never waits on the query below.
  const usernamePending = user?.usernamePending === true;

  // The options object already carries `enabled: !!userId`; composing rather than
  // replacing keeps that guard while adding the auth condition, so the query
  // never fires for an unverified session.
  const eligibility = useQuery({
    ...eligibleExperiencesQueryOptions(userId),
    enabled: !!userId && authStatus === 'verified',
  });

  // `data` is present for a fresh fetch AND for a hydrated persisted cache, which
  // is exactly what "resolved" has to mean for a returning user on cold boot.
  const eligibilityData = eligibility.data;
  const hasEligibilityData = eligibilityData !== undefined;

  // An error on top of usable data is a background-refetch failure, not an
  // unknown: React Query keeps `data` and still reports `status: 'error'`.
  // Treating that as an error would fail a returning user open on every
  // transient network blip, dropping them out of a setup flow mid-way.
  const eligibilityError = eligibility.isError && !hasEligibilityData;

  // Derived, not fetched: the gateway drops the onboarding entry from the
  // eligible list once its status is terminal, so presence is the requirement.
  // `experiences` is raw snake_case wire -- a documented seam exception.
  const onboardingEntry = eligibilityData?.experiences.find(
    (experience) => experience.experience_id === ONBOARDING_EXPERIENCE_ID
  );

  // Auth resolving once is what separates a cold boot (show the splash) from a
  // later `loading` blip (hold what is mounted).
  const hasResolvedOnceRef = useRef(false);
  useEffect(() => {
    if (authStatus !== 'loading') {
      hasResolvedOnceRef.current = true;
    }
  }, [authStatus]);

  // Held across renders so a `loading` blip returns the already-mounted tier
  // instead of remounting the Stack and bouncing a user out of setup on every
  // tab refocus.
  const previousOutcomeRef = useRef<GateOutcome | null>(null);

  // Both refs are read during render on purpose, and only ever consulted while
  // `authStatus === 'loading'` -- a render that by definition follows a
  // committed non-loading one, so the committed value is the current value.
  // Holding them in state instead would make the gate a function of its own
  // previous output and add a render per transition to reach the same answer.
  /* eslint-disable react-hooks/refs -- see above; mirrors src/app/_layout.tsx */
  const hasResolvedOnce = hasResolvedOnceRef.current;
  const previousOutcome = previousOutcomeRef.current;

  const gate = resolveGate({
    authStatus,
    hasResolvedOnce,
    previousOutcome,
    usernamePending,
    onboardingRequired: onboardingEntry !== undefined,
    eligibilityResolved: hasEligibilityData,
    eligibilityError,
  });
  /* eslint-enable react-hooks/refs */

  // Written after commit, so the value read during a render is always the
  // previously committed outcome.
  useEffect(() => {
    previousOutcomeRef.current = gate;
  }, [gate]);

  const currentStep = onboardingEntry?.current_step ?? null;

  return useMemo(
    () => ({
      gate,
      isBooting: gate === 'booting',
      needsSetup: gate === 'setup',
      currentStep,
    }),
    [gate, currentStep]
  );
}

/**
 * Mount above the root layout's `Stack`, inside `AuthProvider` and
 * `QueryProvider` -- it reads the auth session and the eligibility query.
 */
export function AppGateProvider({ children }: { children: React.ReactNode }) {
  const value = useAppGateValue();

  return <AppGateContext.Provider value={value}>{children}</AppGateContext.Provider>;
}

/**
 * The app's one gate answer.
 *
 * Read it instead of re-deriving "is this user still in setup" from auth plus a
 * query -- an independently derived copy is how the ordering bugs in ADR 0006
 * got in.
 */
export function useAppGate(): AppGate {
  const context = useContext(AppGateContext);
  if (!context) {
    throw new Error('useAppGate must be used within AppGateProvider');
  }
  return context;
}
